import { Accordion, AccordionSummary, AccordionDetails, Typography, Divider } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ReactMarkdown from 'react-markdown';
import tokens from '../../../styles/designTokens';
import { body1 } from '../../../styles/typography';
import { SuggestionStatusEnum } from '../../../redux/suggestions';
import { SuggestionIconType } from '../../../models/suggestionStatus.interface';
import { SuggestionStatusButton } from '../../SuggestionStatusButton';
import { CitationsBar } from './citations-bar';
import { useState } from 'react';
import useFeatureFlags from '../../../hooks/useFeatureFlags';

const useStyles = makeStyles(() => ({
  accordion: {
    borderRadius: '0.25rem',
    scrollMarginTop: '2rem', // To account for sticky header
    backgroundColor: tokens.white,
    '&:before': { display: 'none' },
    '&.MuiAccordion-root': {
      padding: 0
    }
  },
  accordionDetails: {
    padding: '0rem 0.75rem 0.75rem  0.75rem',
  },
  accordionSummaryExpanded: {
    padding: '0rem 0.75rem',
  },
  accordionSummaryExpandedNoCitations: {
    '&&, &&.Mui-expanded': {
      minHeight: '1.2rem',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '0.75rem',
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    }
  },
  accordionSummaryExpandedNoCitationsWithSuggestionTitles: {
    '&&, &&.Mui-expanded': {
      minHeight: '1.2rem',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '0.75rem',
    },
    '& .MuiAccordionSummary-content': {
      marginTop: '1.25rem',
    }
  },
  accordionSummary: {
    padding: '0.75rem',
    minHeight: '0px !important',
    alignItems: 'flex-start',
    '&.Mui-expanded': {
      maxHeight: '0px !important',
      '&.MuiButtonBase-root': {
        display: 'none'
      },
    },
    '& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters': {
      margin: '0px !important',
    }
  },
  expandIcon: {
    color: tokens.product100
  },
  previewCollapsed: {
    ...body1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    wordBreak: 'break-word',
    '& p, & ul, & ol, & li': { display: 'inline' },
  },
  collapsedText: {
    ...body1,
    color: tokens.core3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    wordBreak: 'break-word',
    width: 'auto',
    flex: '1 1 0%',
    maxHeight: '6.5em', // 4 lines at ~1.6em line-height
  },
  markdownParagraph: {
    ...body1,
    color: tokens.core3,
    marginBottom: '1.125rem',
  },
  markdownList: {
    ...body1,
    color: tokens.core3,
    marginBottom: '1.125rem',
    paddingLeft: '1.25rem',
    listStyleType: 'disc',
  },
  markdownListItem: {
    ...body1,
    color: tokens.core3,
    marginBottom: '0.5rem',
  },
  markdownStrong: {
    ...body1,
    fontWeight: 400,
    color: tokens.core3,
    display: 'block',
  },
  markdownStrongCollapsed: {
    ...body1,
    fontWeight: 400,
    color: tokens.core3,
    display: 'block',
  },
  suggestionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start', // Ensure children can shrink
    width: '100%'
  },
  suggestionIconsContainer: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: '0.5rem',
    gap: '0.5rem',
  },
  suggestionsIconsContainerExpanded: {
    paddingTop: '0.75rem',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '1rem',
    justifyContent: 'space-around',
  },
  summaryContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },
  summaryTitle: {
    marginBottom: tokens.spacing3xs,
  },
}));

export const HighlightsAccordion = ({
  total,
  index,
  id,
  message,
  recommendation,
  status,
  isExpanded,
  isVisited,
  onHeaderClick,
  onStatusChange,
  handleCitationsClick
}: {
  total: number;
  index: number;
  id: string;
  message: string;
  recommendation: string;
  status: SuggestionStatusEnum;
  isExpanded: boolean;
  isVisited: boolean;
  onHeaderClick: (elementIndex: string) => void;
  onStatusChange: (e: React.MouseEvent<HTMLDivElement, MouseEvent>, id: string, status: SuggestionIconType) => void;
  handleCitationsClick: ({ recommendationIndex, citationIndex }: { recommendationIndex: number; citationIndex: number }) => void;
}) => {
  const borderColor = isExpanded || isVisited ? tokens.neutral90 : tokens.neutral100;
  const classes = useStyles();
  const { getFlag } = useFeatureFlags();
  const permanentShowAddInSuggetionTitles = getFlag('permanentShowAddInSuggetionTitles');

  const scrollIntoView = () => {
    const slot = document.getElementById(`accordion-${id}`);
    slot?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };


  const [currentCitationIndex, setCurrentCitationIndex] = useState<number>(0);


  const getSummaryClass = () => {
    const summaryTitleClass = permanentShowAddInSuggetionTitles ? classes.accordionSummaryExpandedNoCitationsWithSuggestionTitles : classes.accordionSummaryExpandedNoCitations;
    if (isExpanded) {
      if (total > 0) {
        return classes.accordionSummaryExpanded;
      } else {
        return summaryTitleClass;
      }
    } else {
      return classes.accordionSummary;
    }
  };

  return (
    <Accordion
      slots={{ heading: 'div' }}
      id={`accordion-${id}`}
      data-testid={`highlight-accordion-${id}`}
      expanded={isExpanded}
      className={classes.accordion}
      sx={{ border: `1px solid ${borderColor}` }}
      slotProps={{
        transition: { onEntered: scrollIntoView },
        heading: { sx: { display: 'flex', flexDirection: 'row', alignItems: 'center' } },
      }}
    >
      <AccordionSummary
        onClick={() => onHeaderClick(id)} // Prevents the Word add-in from losing focus when clicking the summary
        aria-controls={`highlight-content-${id}`}
        id={`highlight-header-${id}`}
        className={getSummaryClass()}
      >
        <div className={classes.summaryContainer}>
          {permanentShowAddInSuggetionTitles && <div className={classes.summaryTitle}><strong>{id}</strong></div>}
          {!isExpanded && (
            <div className={classes.suggestionsContainer}>
              <div className={classes.previewCollapsed}>
                <ReactMarkdown
                  components={{
                    p: ({ children }) => (
                      <Typography
                        variant="body1"
                        component="p"
                        className={classes.collapsedText}
                      >
                        {children}
                      </Typography>
                    ),
                    strong: ({ children }) => (
                      <strong className={classes.markdownStrongCollapsed}>{children}</strong>
                    ),
                  }}
                >
                  {recommendation}
                </ReactMarkdown>
              </div>
              <div className={classes.suggestionIconsContainer}>
                <SuggestionStatusButton
                  suggestionId={id}
                  status={status}
                  iconType={SuggestionIconType.Ignored}
                  isExpanded={isExpanded}
                  onStatusChange={onStatusChange}
                />
                <SuggestionStatusButton
                  suggestionId={id}
                  status={status}
                  iconType={SuggestionIconType.Completed}
                  isExpanded={isExpanded}
                  onStatusChange={onStatusChange}
                />
              </div>
            </div>
          )}
          {
            (isExpanded && total > 0) && (<CitationsBar
              value={currentCitationIndex + 1}
              total={total}
              handleCitationChange={(citationIndex) => {
                handleCitationsClick({ recommendationIndex: index, citationIndex });
                setCurrentCitationIndex(citationIndex);
              }}
            />)
          }
        </div>
      </AccordionSummary >

      <AccordionDetails className={classes.accordionDetails}>
        <ReactMarkdown
          components={{
            strong: ({ children }) => (
              <strong className={classes.markdownStrong}>{children}</strong>
            ),
            p: ({ children }) => (
              <Typography
                variant="body1"
                className={classes.markdownParagraph}
                component="p"
              >

                {children}
              </Typography>
            ),
            ul: ({ children }) => (
              <Typography
                variant="body1"
                component="ul"
                className={classes.markdownList}
              >
                {children}
              </Typography>
            ),
            ol: ({ children }) => (
              <Typography
                variant="body1"
                component="ol"
                className={classes.markdownList}
              >
                {children}
              </Typography>
            ),
            li: ({ children }) => (
              <Typography
                variant="body1"
                component="li"
                className={classes.markdownListItem}
              >
                {children}
              </Typography>
            ),
          }}
        >
          {message}
        </ReactMarkdown>
        <Divider />
        <div className={classes.suggestionsIconsContainerExpanded}>
          <SuggestionStatusButton
            suggestionId={id}
            status={status}
            iconType={SuggestionIconType.Ignored}
            isExpanded={isExpanded}
            onStatusChange={onStatusChange}
          />
          <SuggestionStatusButton
            suggestionId={id}
            status={status}
            iconType={SuggestionIconType.Completed}
            isExpanded={isExpanded}
            onStatusChange={onStatusChange}
          />
        </div >
      </AccordionDetails >
    </Accordion >
  );
};